/**
 * useIdleTimeout Hook
 *
 * Tracks user activity (mouse, keyboard, scroll, touch, click)
 * and triggers a callback after a period of inactivity.
 *
 * @param {Function} onIdle - Callback when user goes idle
 * @param {Function} onWarning - Callback 1 min before logout
 * @param {number} timeout - Idle timeout in milliseconds (default: 10 min)
 */

import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const WARNING_BEFORE = 60 * 1000;       // 1 minute warning
const THROTTLE_MS = 1000;               // throttle event handling

export default function useIdleTimeout({
  onIdle,
  onWarning,
  timeout = DEFAULT_TIMEOUT,
  enabled = true,
}) {
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const throttleRef = useRef(null);
  const warningFiredRef = useRef(false);

  const resetTimers = useCallback(() => {
    if (!enabled) return;

    lastActivityRef.current = Date.now();
    warningFiredRef.current = false;

    // Clear existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

    // Warning timer
    if (onWarning && timeout > WARNING_BEFORE) {
      warningTimerRef.current = setTimeout(() => {
        warningFiredRef.current = true;
        onWarning();
      }, timeout - WARNING_BEFORE);
    }

    // Idle timer
    timerRef.current = setTimeout(() => {
      onIdle();
    }, timeout);
  }, [onIdle, onWarning, timeout, enabled]);

  const handleActivity = useCallback(() => {
    if (!enabled) return;

    const now = Date.now();
    if (now - (throttleRef.current || 0) < THROTTLE_MS) return;
    throttleRef.current = now;

    resetTimers();
  }, [resetTimers, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach((ev) => window.addEventListener(ev, handleActivity, { passive: true }));

    // Handle visibility change (tab switch)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= timeout) {
          onIdle();
        } else {
          resetTimers();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Start initial timer
    resetTimers();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, [handleActivity, resetTimers, onIdle, timeout, enabled]);

  return {
    resetTimers,
    isWarning: warningFiredRef.current,
  };
}
